import { useState } from 'react';
import type { DeepPartial } from '../../state/editorStore';
import { useEditor } from '../../state/editorStore';
import type { CaptionStyle } from '@captionflow/caption-engine';
import { EMPHASIS_STYLE_PRESETS, type EmphasisStylePreset } from '@captionflow/caption-engine';
import { Row, Slider, Toggle, hex, pct } from './controls';
import { WordLookEditor, WordMotionEditor, type WordLookPatch } from './WordLookControls';

/**
 * Emphasis tab - customise the key word in each caption line (the AI-detected
 * keyword). Everything here writes the project's emphasis style override
 * (`updateStyle`), so it updates the live preview instantly, is undoable, and
 * persists with the project. Three sub-tabs: Templates (preset looks), Style
 * (size, colour, pill, font, fill, outline, glow) and Motion (entrance as the
 * word is spoken + a hold motion). Presets are shared with kMagic. A single
 * word can override all of this from the word inspector.
 */
const PRESETS = EMPHASIS_STYLE_PRESETS;

const ANIMS: { kind: string; label: string }[] = [
  { kind: 'pop', label: 'Pop' },
  { kind: 'scale', label: 'Scale' },
  { kind: 'bounce', label: 'Bounce' },
  { kind: 'slideUp', label: 'Rise' },
  { kind: 'fade', label: 'Fade' },
  { kind: 'none', label: 'None' },
];

const DEFAULT_PILL = '#101216';

export function StudioHookPanel() {
  const emphasis = useEditor((s) => s.resolvedStyle().emphasis);
  const bodyFont = useEditor((s) => s.resolvedStyle().fontFamily);
  const activeKind = useEditor((s) => s.resolvedStyle().activeWord.animation.kind);
  const template = useEditor((s) => s.template());
  const updateStyle = useEditor((s) => s.updateStyle);

  const [tab, setTab] = useState<'templates' | 'style' | 'motion'>('templates');
  // Remember the last pill colour so toggling the pill off then on restores it.
  const [lastPill, setLastPill] = useState(emphasis.highlightColor || DEFAULT_PILL);

  const pillOn = Boolean(emphasis.highlightColor);
  const scale = emphasis.scale ?? 1;

  const setEmph = (patch: DeepPartial<CaptionStyle['emphasis']>) => updateStyle({ emphasis: patch });
  // The advanced look fields a preset doesn't define are cleared (null) so
  // switching presets never leaves stray italics / metal / glow behind. The
  // key-word MOTION is kept - it's chosen separately.
  const CLEAR_LOOK = { fontWeight: null, italic: null, fill: null, gradient: null, stroke: null, glow: null } as const;
  // Apply a preset as a COMPLETE emphasis look: always set newLine + fontFamily
  // (defaulting to the caption body font = no accent) so switching presets never
  // leaves a stray drop / script font from the previous one.
  const applyPreset = (p: EmphasisStylePreset) =>
    updateStyle({
      emphasis: {
        ...CLEAR_LOOK,
        color: p.patch.color ?? '#ffd400',
        highlightColor: p.patch.highlightColor ?? '',
        bold: p.patch.bold ?? true,
        scale: p.patch.scale ?? 1,
        newLine: p.patch.newLine ?? false,
        fontFamily: p.patch.fontFamily ?? bodyFont,
      },
    } as DeepPartial<CaptionStyle>);

  // Restore the emphasis to whatever the CURRENT caption template ships (many
  // templates define their own key-word look).
  const resetToCaption = () => {
    const t = template.style.emphasis;
    updateStyle({
      emphasis: {
        ...CLEAR_LOOK,
        motion: null,
        color: t.color ?? '#ffd400',
        highlightColor: t.highlightColor ?? '',
        bold: t.bold,
        scale: t.scale,
        newLine: t.newLine ?? false,
        fontFamily: t.fontFamily ?? bodyFont,
      },
    } as DeepPartial<CaptionStyle>);
  };

  const matches = (p: EmphasisStylePreset) =>
    (emphasis.color ?? '').toLowerCase() === (p.patch.color ?? '').toLowerCase() &&
    (emphasis.highlightColor ?? '').toLowerCase() === (p.patch.highlightColor ?? '').toLowerCase();

  // The accent font equal to the body font means "no accent".
  const lookValue = {
    fontFamily: emphasis.fontFamily && emphasis.fontFamily !== bodyFont ? emphasis.fontFamily : null,
    fontWeight: emphasis.fontWeight,
    italic: emphasis.italic,
    fill: emphasis.fill,
    gradient: emphasis.gradient,
    stroke: emphasis.stroke,
    glow: emphasis.glow,
  };
  const onLook = (p: WordLookPatch) => {
    const { highlightColor: _pill, ...rest } = p;
    setEmph({ ...rest, ...(p.fontFamily === null ? { fontFamily: bodyFont } : {}) } as DeepPartial<CaptionStyle['emphasis']>);
  };

  return (
    <div className="slp-body shk">

      <div className="shk-subtabs">
        {(['templates', 'style', 'motion'] as const).map((t) => (
          <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
            {t === 'templates' ? 'Templates' : t === 'style' ? 'Style' : 'Motion'}
          </button>
        ))}
      </div>

      {tab === 'templates' && (
        <div className="shk-presets">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              className={matches(p) ? 'shk-preset on' : 'shk-preset'}
              onClick={() => applyPreset(p)}
              title={p.name}
            >
              <span className="shk-preset-stage">
                <span
                  className="shk-chip"
                  style={{
                    color: p.patch.color,
                    fontWeight: p.patch.bold ? 800 : 600,
                    fontFamily: p.patch.fontFamily,
                    fontSize: (p.patch.scale ?? 1) >= 1.3 ? 20 : undefined,
                    ...(p.patch.highlightColor
                      ? { background: p.patch.highlightColor, padding: '3px 9px', borderRadius: 8 }
                      : null),
                  }}
                >
                  {p.patch.fontFamily === 'Caveat' ? 'key' : '10%'}
                </span>
              </span>
              <span className="shk-preset-name">{p.name}</span>
            </button>
          ))}
        </div>
      )}
      {tab === 'templates' && (
        <button className="shk-reset" onClick={resetToCaption}>
          Reset to caption style
        </button>
      )}

      {tab === 'style' && (
        <div className="shk-fields">
          <Slider label="Size" min={0.85} max={2} step={0.02} value={scale}
            onChange={(v) => setEmph({ scale: v })} display={pct(scale)} />
          <Row label="Bold">
            <Toggle on={!!emphasis.bold} onClick={() => setEmph({ bold: !emphasis.bold })} />
          </Row>
          <Row label="Own line">
            <Toggle on={!!emphasis.newLine} onClick={() => setEmph({ newLine: !emphasis.newLine })} />
          </Row>
          <Row label="Text color">
            <input type="color" className="shk-color" value={hex(emphasis.color, '#ffd400')}
              onChange={(e) => setEmph({ color: e.target.value })} />
          </Row>
          <Row label="Pill background">
            <Toggle
              on={pillOn}
              onClick={() => {
                if (pillOn) {
                  setLastPill(emphasis.highlightColor || DEFAULT_PILL);
                  setEmph({ highlightColor: '' });
                } else {
                  setEmph({ highlightColor: lastPill });
                }
              }}
            />
          </Row>
          {pillOn && (
            <Row label="Pill color">
              <input type="color" className="shk-color" value={hex(emphasis.highlightColor, DEFAULT_PILL)}
                onChange={(e) => {
                  setLastPill(e.target.value);
                  setEmph({ highlightColor: e.target.value });
                }} />
            </Row>
          )}
          <div className="ses-divider" />
          <WordLookEditor value={lookValue} onChange={onLook} inheritFont="Caption font" />
        </div>
      )}

      {tab === 'motion' && (
        <div className="shk-fields">
          <span className="shk-sublabel">Every key word, as it's spoken</span>
          <WordMotionEditor value={emphasis.motion} offLabel="Off"
            onChange={(m) => setEmph({ motion: m } as DeepPartial<CaptionStyle['emphasis']>)} />

          <div className="ses-divider" />
          <span className="shk-sublabel">Every spoken word (pop as it's said)</span>
          <div className="shk-anims">
            {ANIMS.map((a) => (
              <button
                key={a.kind}
                className={activeKind === a.kind ? 'on' : ''}
                onClick={() => updateStyle({ activeWord: { animation: { kind: a.kind } } } as never)}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
